import React, { useEffect, useState } from 'react';
import BlankTemplate from 'components/Templates/BlankTemplate';
import TransferEmployee from 'components/Organisms/Employee/EmployeeRecord/ActionList/TransferEmployee';
import { useThunk } from 'hooks/useThunk';
import { fetchTransferEmployee } from 'store';
import { Typography, Box, Skeleton } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { transferEmployeeAction } from 'api/employees';
import dayjs from 'dayjs';
import useToast from 'components/Provider/useToast';

const TransferEmployeePage = () => {
  const addToast = useToast();
  const navigate = useNavigate();
  const { userId } = useParams();

  const [loading, setLoading] = useState(false);
  const [getTransferEmployee, isLoadingTransferEmployee, loadingTransferEmployeeError] =
    useThunk(fetchTransferEmployee);

  useEffect(() => {
    const query = new URLSearchParams({});
    query.append('id', userId);
    getTransferEmployee(query);
  }, [userId, getTransferEmployee]);

  const { transferEmployee } = useSelector((state) => state.employees);

  const employeeData = {
    supervisor: transferEmployee?.employeeInstance?.supervisor?.fullName,
    department: transferEmployee?.employeeInstance?.department?.name,
    designation: transferEmployee?.employeeInstance?.designation?.name,
    location: transferEmployee?.employeeInstance?.location?.name,
  };

  const supervisorOptions = transferEmployee?.employeeList;
  const departmentOptions = transferEmployee?.departmentsList;
  const designationOptions = transferEmployee?.designationsList;
  const locationOptions = transferEmployee?.locationsList;
  const userData = transferEmployee?.user;

  const handleSubmit = async ({ effectiveStartDate, supervisor, department, designation, location, notes }) => {
    const newStartDate = effectiveStartDate ? dayjs(effectiveStartDate).format('MM/DD/YYYY') : '';
    const newNotes = notes || '';
    const bodyFormData = new FormData();
    bodyFormData.append('empID', transferEmployee?.user?.employeeId);
    bodyFormData.append('CurrentAction', 'SaveAndSubmit');
    bodyFormData.append('effectiveStartDate', newStartDate);
    bodyFormData.append('employee.supervisor.id', supervisor);
    bodyFormData.append('employee.department.id', department);
    bodyFormData.append('employee.designation.id', designation);
    bodyFormData.append('employee.location.id', location);
    bodyFormData.append('notes', newNotes);

    try {
      setLoading(true);
      const response = await transferEmployeeAction(bodyFormData);
      if (response?.data?.status === 'SUCCESS') {
        addToast({ type: 'success', title: 'SUCCESS', message: response?.data?.message });
        navigate(`/company/employee/view/${userId}`);
      } else {
        addToast({ type: 'error', title: 'ERROR', message: response?.data?.message });
      }
    } catch (error) {
      addToast({ type: 'error', title: 'ERROR', message: error?.response?.data?.message || error?.message });
    } finally {
      setLoading(false);
    }
  };

  if (loadingTransferEmployeeError !== null) {
    return (
      <Typography variant="h6">
        {loadingTransferEmployeeError?.message || `Error while loading details for employee id : ${userId}.`}
      </Typography>
    );
  }

  const breadcrumbData = [
    { id: 1, name: 'Employee List', path: '/company/employee/employees-list', isActive: false },
    { id: 2, name: 'Employee Records', path: `/company/employee/view/${userId}`, isActive: false },
    { id: 2, name: 'Transfer Employee', isActive: true },
  ];

  return isLoadingTransferEmployee ? (
    <Box sx={{ p: 4 }}>
      <Skeleton height={70} />
      <Skeleton />
      <Skeleton />
    </Box>
  ) : (
    <BlankTemplate
      pageHeading={`Transfer Employee: ${get(transferEmployee, 'employeeInstance.fullName', '')} (${get(
        transferEmployee,
        'employeeInstance.profile.employeeId',
        'NA',
      )})`}
      breadcrumbData={breadcrumbData}
      padding="3rem"
    >
      <TransferEmployee
        employeeData={employeeData}
        supervisorOptions={supervisorOptions}
        departmentOptions={departmentOptions}
        designationOptions={designationOptions}
        locationOptions={locationOptions}
        userData={userData}
        handleSubmit={handleSubmit}
        loading={loading}
        btnLabel="Transfer"
      />
    </BlankTemplate>
  );
};

export default TransferEmployeePage;
